import { useState, useRef } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input, { type InputRef } from "../../components/core/input.core";
import Button from "../../components/core/button.core";
import { useEmailSendOtpBrandHttp } from "../../hooks/http/auth/email-send-otp.hook";
import type { IOtp } from "../../../types/models/otp.models.types";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";

// Define the form schema with Zod
const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

// Infer the type from the schema
type LoginFormValues = z.infer<typeof loginSchema>;

export default function EmailLoginForm(props: {
  sentEmail: boolean;
  setSentEmail: (value: boolean) => void;
  setAuthenticationState: React.Dispatch<React.SetStateAction<IOtp>>;
}) {
  const [formErrors] = useState<string[]>([]);
  const emailRef = useRef<InputRef>(null);
  const { execute: sendOtpHttp, fetching: sendingOtp } =
    useEmailSendOtpBrandHttp();

  const { toast } = useToast();

  const emailForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmitEmail = emailForm.handleSubmit(async (data) => {
    const response = await sendOtpHttp({ body: { email: data.email } });
    if (!response.success) {
      handleHttpError(response, toast);
      return;
    }
    props.setAuthenticationState(response.data);
    props.setSentEmail(true);
  });

  return (
    <div className="flex flex-col items-center gap-10 bg-white px-5 pt-10 w-full h-screen sm:h-fit">
      <form
        onSubmit={onSubmitEmail}
        className="flex flex-col justify-between w-full max-w-md h-full"
      >
        <div className="flex flex-col w-full">
          <h1 className="pb-10 font-cabinetgrotesk font-medium text-3xl tracking-tight">
            Get started !
          </h1>

          {formErrors.length > 0 && (
            <div className="bg-red-100 mb-4 px-4 py-3 border border-red-400 rounded text-red-700">
              {formErrors.map((error, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <Controller
            control={emailForm.control}
            name="email"
            render={({ field, fieldState }) => (
              <Input
                ref={emailRef}
                label="Work email-id"
                placeholder="xyz@workmail.com"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                validateAs="email"
                className="py-4"
                type="email"
              />
            )}
          />
        </div>

        <div className="justify-center items-center pt-8 pb-4 w-full">
          <Button
            className="w-full font-semibold font-sm"
            label="Continue"
            type="button"
            onClick={onSubmitEmail}
            isLoading={sendingOtp}
          />
        </div>
      </form>
    </div>
  );
}
